// Garment Resizer — the "Jersey Master Applicator" tool. A single page with
// three stages:
//   1. UPLOAD    — master EPS (single) + size templates (many .eps)
//   2. PROCESSING — poll the job until every size finishes
//   3. RESULTS   — per-size proof + EPS download, plus a "download all" ZIP
//
// Auth: the admin API is cookie/session authed (requireAdmin reads the sealed
// session cookie). Same-origin <img>/<a> requests carry that cookie
// automatically, so proof thumbnails and download anchors don't need a token.
// JSON calls go through the shared apiFetch helper; multipart uploads use a
// small local fetch (apiFetch forces application/json on bodies, which would
// break FormData boundaries) that mirrors apiFetch's auth + credentials.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'

const BASE = import.meta.env.VITE_API_URL || ''

// The 11 sizes the tool produces, in the spec's display order.
const SIZES = ['Y6', 'Y8', 'Y10', 'Y12', 'Y14', 'Y16', 'XS', 'S', 'L', 'XL', '2XL'] as const

// The 3 spot colours every master jersey must carry.
const REQUIRED_SPOTS = ['PANTONE Process Black C', 'FLPINK', 'FLYELLOW']

interface JerseyRegion {
  x0: number; y0: number; x1: number; y1: number
  width: number; height: number; source: string
}
interface SpotColourCheck {
  hasDocumentCustomColors: boolean
  present: string[]
  missing: string[]
}
interface MasterUpload {
  masterId: string
  filename: string
  region: JerseyRegion
  spot: SpotColourCheck
}
interface BoundingBox { x0: number; y0: number; x1: number; y1: number }
interface TemplateRow { templateId: string; filename: string; size: string; box: BoundingBox }
interface TemplatesUpload { templates: TemplateRow[]; unrecognized: string[] }
interface LibraryTemplate { size: string; filename: string; box: BoundingBox }
interface LibraryResponse { templates: LibraryTemplate[]; count: number }

type SizeStatus = 'waiting' | 'processing' | 'done' | 'error'
interface JobResult {
  size: string
  status: SizeStatus
  scalePct: number | null
  offsetX: number | null
  offsetY: number | null
  aspectMatch: number | null
  error?: string
}
interface JobState {
  jobId: string
  status: 'processing' | 'done' | 'error'
  results: JobResult[]
}

// Multipart upload that mirrors apiFetch's auth (Bearer token + cookie) but
// lets the browser set the multipart Content-Type/boundary itself.
async function uploadFetch<T>(getToken: () => Promise<string | undefined>, path: string, form: FormData): Promise<T> {
  let token: string | undefined
  try { token = await getToken() } catch { /* anon */ }
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(`${BASE}${path}`, { method: 'POST', body: form, headers, credentials: 'include' })
  const text = await res.text()
  let body: unknown = null
  if (text) { try { body = JSON.parse(text) } catch { /* non-JSON */ } }
  if (!res.ok) {
    const b = body as { message?: string; error?: string } | null
    throw new Error(b?.message ?? b?.error ?? `HTTP ${res.status}`)
  }
  return body as T
}

function num(n: number | null, digits = 0): string {
  if (n == null) return '—'
  return n.toFixed(digits)
}

export default function GarmentResizer() {
  const { getAccessToken } = useAuth()
  const getToken = getAccessToken as unknown as () => Promise<string | undefined>

  type Stage = 'upload' | 'processing' | 'results'
  const [stage, setStage] = useState<Stage>('upload')

  // Upload stage state
  const [master, setMaster] = useState<MasterUpload | null>(null)
  const [masterBusy, setMasterBusy] = useState(false)
  const [masterErr, setMasterErr] = useState<string | null>(null)

  const [templates, setTemplates] = useState<TemplateRow[]>([])
  const [unrecognized, setUnrecognized] = useState<string[]>([])
  const [tplBusy, setTplBusy] = useState(false)
  const [tplErr, setTplErr] = useState<string | null>(null)

  // Stored template library (sizes already on the server).
  const [librarySizes, setLibrarySizes] = useState<string[]>([])

  const [processErr, setProcessErr] = useState<string | null>(null)
  const [fillHemCorners, setFillHemCorners] = useState(true)
  const [mergeIntoTemplate, setMergeIntoTemplate] = useState(false)

  // Processing / results state
  const [job, setJob] = useState<JobState | null>(null)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const masterInput = useRef<HTMLInputElement>(null)
  const tplInput = useRef<HTMLInputElement>(null)

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current) }, [])

  // Load the stored template library once on mount so the size grid can show
  // which sizes are already covered without an upload.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const d = await apiFetch<LibraryResponse>(getToken, '/api/admin/garment-resizer/templates/library')
        if (!cancelled) setLibrarySizes(d.templates.map((t) => t.size))
      } catch { /* empty library — fall back to the upload flow */ }
    })()
    return () => { cancelled = true }
  }, [getToken])

  // ---- Master upload -------------------------------------------------------
  async function onMasterFiles(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    setMasterBusy(true); setMasterErr(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const d = await uploadFetch<MasterUpload>(getToken, '/api/admin/garment-resizer/upload/master', form)
      setMaster(d)
    } catch (e) { setMasterErr((e as Error).message); setMaster(null) }
    finally { setMasterBusy(false) }
  }

  // ---- Templates upload ----------------------------------------------------
  async function onTemplateFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setTplBusy(true); setTplErr(null)
    try {
      const form = new FormData()
      for (const f of Array.from(files)) form.append('files', f)
      const d = await uploadFetch<TemplatesUpload>(getToken, '/api/admin/garment-resizer/upload/templates', form)
      // Merge with anything already staged; de-dupe by size (latest wins).
      setTemplates((prev) => {
        const bySize = new Map<string, TemplateRow>()
        for (const t of prev) bySize.set(t.size, t)
        for (const t of d.templates) bySize.set(t.size, t)
        return Array.from(bySize.values())
      })
      setUnrecognized(d.unrecognized)
    } catch (e) { setTplErr((e as Error).message) }
    finally { setTplBusy(false) }
  }

  // ---- Process -------------------------------------------------------------
  async function processAll() {
    if (!canProcess) return
    setProcessErr(null)
    try {
      const d = await apiFetch<{ jobId: string }>(getToken, '/api/admin/garment-resizer/process', {
        method: 'POST',
        body: JSON.stringify({
          masterId: master!.masterId,
          useLibrary: true,
          templateIds: templates.map((t) => t.templateId),
          fillHemCorners,
          mergeIntoTemplate,
        }),
      })
      // Union of uploaded sizes + stored-library sizes (uploaded overrides).
      const sizes = SIZES.filter((s) => effectiveSizes.has(s))
      setJob({ jobId: d.jobId, status: 'processing', results: sizes.map((size) => ({
        size, status: 'waiting', scalePct: null, offsetX: null, offsetY: null, aspectMatch: null,
      })) })
      setStage('processing')
      poll(d.jobId)
    } catch (e) { setProcessErr((e as Error).message) }
  }

  // ---- Poll the job until it's no longer 'processing' ----------------------
  const poll = useCallback((jobId: string) => {
    async function tick() {
      try {
        const j = await apiFetch<JobState>(getToken, `/api/admin/garment-resizer/job/${jobId}`)
        setJob(j)
        if (j.status === 'processing') {
          pollRef.current = setTimeout(tick, 1000)
        } else {
          setStage('results')
        }
      } catch {
        // Transient error — keep polling rather than bailing the whole job.
        pollRef.current = setTimeout(tick, 1500)
      }
    }
    pollRef.current = setTimeout(tick, 1000)
  }, [getToken])

  function reset() {
    if (pollRef.current) clearTimeout(pollRef.current)
    setStage('upload')
    setMaster(null); setMasterErr(null)
    setTemplates([]); setUnrecognized([]); setTplErr(null)
    setProcessErr(null); setJob(null)
    if (masterInput.current) masterInput.current.value = ''
    if (tplInput.current) tplInput.current.value = ''
  }

  const recognizedSizes = new Set(templates.map((t) => t.size))
  const librarySet = new Set(librarySizes)
  // Every size that will be produced: uploaded ∪ stored-library.
  const effectiveSizes = new Set<string>([...recognizedSizes, ...librarySet])
  const canProcess = !!master && effectiveSizes.size > 0

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Production</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Garment Resizer</h1>
          <p className="text-ink-2 text-sm mt-1 max-w-2xl">Jersey Master Applicator. Upload one master EPS plus the size-template EPS files, then apply the master artwork to every size in one pass — scaled and positioned to each template's jersey region. Download applied EPS files individually or as a ZIP.</p>
        </div>
        {stage !== 'upload' && (
          <button onClick={reset} className="btn-outline">Start over</button>
        )}
      </div>

      {/* Stage indicator */}
      <div className="flex items-center gap-2 mb-6 text-xs uppercase tracking-wide font-semibold">
        {(['upload', 'processing', 'results'] as const).map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            {i > 0 && <span className="text-ink-3">→</span>}
            <span className={'px-2.5 py-1 rounded ' + (stage === s ? 'bg-gold/15 text-gold-dark' : 'text-ink-3')}>
              {i + 1}. {s}
            </span>
          </div>
        ))}
      </div>

      {/* ================= STAGE 1: UPLOAD ================= */}
      {stage === 'upload' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* --- Master --- */}
          <div className="card">
            <div className="eyebrow">Master</div>
            <h2 className="font-heading uppercase font-bold text-xl text-ink">Master EPS</h2>
            <p className="text-ink-2 text-sm mt-1">A single EPS holding the artwork to apply across every size.</p>

            <input ref={masterInput} type="file" accept=".eps,application/postscript" className="hidden"
              onChange={(e) => onMasterFiles(e.target.files)} />
            <button onClick={() => masterInput.current?.click()} disabled={masterBusy}
              className="mt-3 w-full border-2 border-dashed border-border rounded-lg py-8 text-sm text-ink-3 hover:border-gold hover:text-gold-dark transition-colors disabled:opacity-50">
              {masterBusy ? 'Uploading…' : master ? `Replace — ${master.filename}` : 'Click to choose a master .eps'}
            </button>

            {masterErr && <div className="mt-3 text-sm text-red-700">{masterErr}</div>}

            {master && (
              <div className="mt-4 space-y-3">
                {/* Detected jersey region */}
                <div className="rounded border border-border bg-surface px-3 py-2 text-sm">
                  <div className="text-[10px] uppercase tracking-wider text-ink-3">Detected jersey region</div>
                  <div className="text-ink mt-0.5">
                    {num(master.region.width)} × {num(master.region.height)} pt
                  </div>
                  <div className="text-xs text-ink-3 mt-0.5">source: {master.region.source}</div>
                </div>

                {/* Spot-colour check */}
                <div className="rounded border border-border bg-surface px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1.5">Spot colours</div>
                  <ul className="space-y-1">
                    {REQUIRED_SPOTS.map((name) => {
                      const ok = master.spot.present.includes(name)
                      return (
                        <li key={name} className="flex items-center gap-2 text-sm">
                          <span className={ok ? 'text-emerald-600' : 'text-red-600'}>{ok ? '✓' : '✗'}</span>
                          <span className={ok ? 'text-ink' : 'text-red-700'}>{name}</span>
                        </li>
                      )
                    })}
                  </ul>
                  {master.spot.missing.length > 0 && (
                    <div className="text-xs text-red-700 mt-2">Missing: {master.spot.missing.join(', ')}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* --- Templates --- */}
          <div className="card">
            <div className="eyebrow">Templates</div>
            <h2 className="font-heading uppercase font-bold text-xl text-ink">Size templates</h2>
            {librarySizes.length > 0 ? (
              <p className="text-ink-2 text-sm mt-1">Using {librarySizes.length} stored {librarySizes.length === 1 ? 'template' : 'templates'} — just upload your master. Stored sizes are applied automatically.</p>
            ) : (
              <p className="text-ink-2 text-sm mt-1">One EPS per size. Filenames are matched to a size; each cell lights up as it's recognised.</p>
            )}

            <input ref={tplInput} type="file" accept=".eps,application/postscript" multiple className="hidden"
              onChange={(e) => onTemplateFiles(e.target.files)} />
            <button onClick={() => tplInput.current?.click()} disabled={tplBusy}
              className="mt-3 w-full border-2 border-dashed border-border rounded-lg py-8 text-sm text-ink-3 hover:border-gold hover:text-gold-dark transition-colors disabled:opacity-50">
              {tplBusy ? 'Uploading…' : librarySizes.length > 0 ? 'Optional: upload templates to add or override stored ones' : 'Click to choose template .eps files'}
            </button>

            {tplErr && <div className="mt-3 text-sm text-red-700">{tplErr}</div>}

            {/* Size grid — uploaded (override) > stored library > missing */}
            <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-1.5">
              {SIZES.map((size) => {
                const uploaded = recognizedSizes.has(size)
                const stored = librarySet.has(size)
                const cls = uploaded
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                  : stored
                  ? 'bg-emerald-50/60 border-emerald-200 text-emerald-700'
                  : 'bg-surface border-border text-ink-3'
                return (
                  <div key={size}
                    className={'relative text-center text-xs font-semibold uppercase rounded py-2 border ' + cls}>
                    {size}
                    {!uploaded && stored && (
                      <span className="block text-[8px] font-bold tracking-wider text-emerald-600 normal-case mt-0.5">stored</span>
                    )}
                    {uploaded && stored && (
                      <span className="block text-[8px] font-bold tracking-wider text-emerald-600 normal-case mt-0.5">override</span>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="text-xs text-ink-3 mt-2">{effectiveSizes.size} of {SIZES.length} sizes ready{recognizedSizes.size > 0 ? ` (${recognizedSizes.size} uploaded)` : ''}</div>

            {unrecognized.length > 0 && (
              <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <div className="font-semibold mb-1">Unrecognised ({unrecognized.length})</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {unrecognized.map((f) => <li key={f} className="truncate">{f}</li>)}
                </ul>
              </div>
            )}
          </div>

          {/* Process bar */}
          <div className="lg:col-span-2">
            {processErr && <div className="card text-red-700 mb-3">{processErr}</div>}
            <label className="flex items-start gap-2 mb-3 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={fillHemCorners}
                onChange={(e) => setFillHemCorners(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Fill hem corners</span>
                <span className="block text-xs text-ink-3">
                  Extend the side-panel colours into the bottom hem corners where the master’s white piping
                  is exposed. Leave on for cut-and-sew jerseys; turn off to keep the master exactly as drawn.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 mb-3 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={mergeIntoTemplate}
                onChange={(e) => setMergeIntoTemplate(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Merge into original template</span>
                <span className="block text-xs text-ink-3">
                  Keep the supplied template’s Illustrator artboard and structure, with the design merged in —
                  a to-scale production drop-in (renders in any RIP). Turn off for a flattened EPS that opens
                  with the artwork editable in Illustrator.
                </span>
              </span>
            </label>
            <button onClick={processAll} disabled={!canProcess}
              className="btn-gold w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed">
              Process All {effectiveSizes.size > 0 ? `(${effectiveSizes.size} ${effectiveSizes.size === 1 ? 'size' : 'sizes'})` : ''}
            </button>
            {!canProcess && (
              <p className="text-xs text-ink-3 mt-2 text-center">
                {librarySizes.length > 0
                  ? 'Upload a master EPS to enable processing — the stored templates are applied automatically.'
                  : 'Upload a master EPS and at least one recognised template to enable processing.'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ================= STAGE 2: PROCESSING ================= */}
      {stage === 'processing' && job && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="font-heading uppercase font-bold text-lg text-ink">Processing…</div>
            <div className="h-2 w-2 rounded-full bg-gold animate-pulse" />
          </div>
          <TransformTable results={job.results} />
        </div>
      )}

      {/* ================= STAGE 3: RESULTS ================= */}
      {stage === 'results' && job && (
        <div>
          {job.status === 'error' && (
            <div className="card text-red-700 mb-4">Processing failed. You can start over and try again.</div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="font-heading uppercase font-bold text-lg text-ink">
              Results — {job.results.filter((r) => r.status === 'done').length} of {job.results.length} ready
            </div>
            {job.results.some((r) => r.status === 'done') && (
              <a href={`${BASE}/api/admin/garment-resizer/download/${job.jobId}/all`}
                className="btn-gold">Download all as ZIP</a>
            )}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {job.results.map((r) => (
              <div key={r.size} className="card">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="font-heading uppercase font-bold text-lg text-ink">{r.size}</div>
                  <StatusPill status={r.status} />
                </div>

                {r.status === 'done' ? (
                  <>
                    <div className="bg-surface border border-border rounded overflow-hidden flex items-center justify-center aspect-square mb-3">
                      <img src={`${BASE}/api/admin/garment-resizer/proof/${job.jobId}/${encodeURIComponent(r.size)}`}
                        alt={`${r.size} proof`} className="max-w-full max-h-full object-contain" />
                    </div>
                    <a href={`${BASE}/api/admin/garment-resizer/download/${job.jobId}/${encodeURIComponent(r.size)}`}
                      className="btn-outline w-full justify-center text-sm">Download EPS</a>
                  </>
                ) : r.status === 'error' ? (
                  <div className="text-sm text-red-700">{r.error ?? 'Failed to apply master to this size.'}</div>
                ) : (
                  <div className="text-sm text-ink-3">{r.status === 'processing' ? 'Applying…' : 'Waiting…'}</div>
                )}
              </div>
            ))}
          </div>

          {/* Transform table for the full run */}
          <div className="card">
            <div className="eyebrow">Transforms</div>
            <TransformTable results={job.results} />
          </div>
        </div>
      )}
    </div>
  )
}

function StatusPill({ status }: { status: SizeStatus }) {
  const map: Record<SizeStatus, string> = {
    waiting: 'bg-gray-100 text-gray-600',
    processing: 'bg-amber-50 text-amber-700',
    done: 'bg-emerald-50 text-emerald-700',
    error: 'bg-red-50 text-red-700',
  }
  return (
    <span className={'text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ' + map[status]}>
      {status}
    </span>
  )
}

function TransformTable({ results }: { results: JobResult[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-ink-3 border-b border-border">
            <th className="py-2 pr-3 font-bold">Size</th>
            <th className="py-2 pr-3 font-bold">Status</th>
            <th className="py-2 pr-3 font-bold">Scale %</th>
            <th className="py-2 pr-3 font-bold">Offset X</th>
            <th className="py-2 pr-3 font-bold">Offset Y</th>
            <th className="py-2 pr-3 font-bold">Aspect match %</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.size} className="border-b border-border/60">
              <td className="py-2 pr-3 font-semibold text-ink">{r.size}</td>
              <td className="py-2 pr-3"><StatusPill status={r.status} /></td>
              <td className="py-2 pr-3 text-ink-2">{num(r.scalePct, 1)}</td>
              <td className="py-2 pr-3 text-ink-2">{num(r.offsetX, 1)}</td>
              <td className="py-2 pr-3 text-ink-2">{num(r.offsetY, 1)}</td>
              <td className="py-2 pr-3 text-ink-2">{num(r.aspectMatch, 1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
